import { useState, useEffect } from "react";
import { useScrollNavigation } from "@/contexts/ScrollContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";

interface ScrollProgressProps {
  currentSection: number;
  totalSections: number;
}

const sectionKeys = [
  "nav.home",
  "scroll.pain",
  "nav.expertise",
  "scroll.process",
  "nav.portfolio",
  "scroll.why",
  "scroll.stats",
  "scroll.testimonials",
  "scroll.cta",
];

const ScrollProgress = ({ currentSection, totalSections }: ScrollProgressProps) => {
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  const [mobileScrollProgress, setMobileScrollProgress] = useState(0);
  const { goToSection } = useScrollNavigation();
  
  // Desktop progress based on sections
  const desktopProgress = Math.round(((currentSection + 1) / totalSections) * 100);
  
  // Mobile progress based on actual scroll
  useEffect(() => {
    if (!isMobile) return;
    
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
      setMobileScrollProgress(Math.min(scrollPercentage, 100));
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);
  
  const progress = isMobile ? mobileScrollProgress : desktopProgress;

  // Mobile: Simple percentage indicator
  if (isMobile) {
    return (
      <div className="fixed right-4 top-1/2 -translate-y-1/2 z-[100]">
        <div className="relative">
          {/* Circular background */}
          <div className="w-12 h-12 rounded-full bg-card/80 backdrop-blur-sm border border-border/30 flex items-center justify-center shadow-lg">
            <div className="text-xs font-bold text-foreground tabular-nums">
              {progress}%
            </div>
          </div>
          {/* Progress ring */}
          <svg className="absolute inset-0 w-12 h-12 -rotate-90" viewBox="0 0 48 48">
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke="hsl(var(--border))"
              strokeWidth="2"
              opacity="0.2"
            />
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke="hsl(var(--holographic-cyan))"
              strokeWidth="2"
              strokeDasharray={`${2 * Math.PI * 20}`}
              strokeDashoffset={`${2 * Math.PI * 20 * (1 - progress / 100)}`}
              strokeLinecap="round"
              className="transition-all duration-300"
            />
          </svg>
        </div>
      </div>
    );
  }
  
  // Desktop: Section dots with navigation
  return (
    <div className="fixed right-3 top-1/2 -translate-y-1/2 z-[100] flex flex-col items-center gap-0">
      {/* Ultra-thin vertical line */}
      <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-holographic-cyan/30 to-transparent" />
      
      {/* Section Dots */}
      <div className="flex flex-col gap-5 relative">
        {Array.from({ length: totalSections }).map((_, index) => (
          <button
            key={index}
            onClick={() => goToSection(index)}
            className="group relative w-3 h-3 transition-all duration-300"
            aria-label={`Go to section ${index + 1}`}
          >
            {/* Active dot with glow */}
            {index === currentSection ? (
              <>
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[hsl(var(--holographic-cyan))] to-[hsl(var(--holographic-pink))]" />
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[hsl(var(--holographic-cyan))] to-[hsl(var(--holographic-pink))] blur-sm opacity-70 animate-pulse" />
              </>
            ) : (
              <div className="absolute inset-0 rounded-full bg-border/40 group-hover:bg-border/70 transition-colors duration-200" />
            )}
            
            {/* Section name tooltip on hover */}
            <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
              <div className="px-2.5 py-1 rounded bg-background/95 backdrop-blur-sm border border-border/50 text-xs text-muted-foreground">
                {t(sectionKeys[index] || `Section ${index + 1}`)}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Mini Percentage Counter */}
      <div className="mt-6 relative">
        <div className="text-xs font-bold text-holographic tabular-nums">
          {progress}%
        </div>
      </div>
    </div>
  );
};

export default ScrollProgress;
